/**
 * MYAC-954 — the four register stores, from ONE factory, reading the read-model.
 *
 * They used to be four copy-paste siblings, each subscribing to
 * `projects/{pid}/memories` (the engine's own document — `run_report`, raw provenance,
 * session ids) plus `projects/{pid}/assessments` for live status, and each sorting the
 * memory versions in the browser to find the latest. All of that is gone:
 *
 *   rows/columns/numbers → projects/{pid}/views/registers_nav + registers_rows
 *   running / failed     → projects/{pid}/views/registers_status
 *
 * The public surface is deliberately UNCHANGED (`rows`, `columns`, `isRunning`,
 * `runFailed`, `usedFallback`, `runSummary`, `hasExtraction`, `recordCount`,
 * `sourceDocuments`, `extract`) so no component had to move with it.
 *
 * §3.2 — nothing is reduced here: `recordCount` is the projected number, ordering is the
 * projected `position`. §3.7 — an empty table renders only after a SUCCESSFUL empty read;
 * `loadError` is what tells the view the difference.
 */
import { computed, ref, watch, type Ref } from 'vue'
import { defineStore } from 'pinia'
import {
  collection, limit, onSnapshot, orderBy, query, serverTimestamp, updateDoc, where,
  type DocumentReference, type Unsubscribe,
} from 'firebase/firestore'
import { useFirestore } from 'vuefire'
import { useViewDoc, useViewRows } from '@/composables/useClientViews'
import { useAssessmentsStore } from '@/stores/assessments'
import { logger } from '@/utils/logger'
import { trackAction } from '@/composables/useActivityTracker'
import { CsmAction } from '@/constants/csmActions'
import type { DocumentHandle } from '@/models/DocumentHandle'
import { DOCUMENT_HANDLES_KEY, type RegisterRow as RegisterRecord } from '@/utils/registers'

/** Register keys, as the backend writes them. */
export type RegisterKey = 'capa' | 'nc' | 'supplier' | 'change_control'

export const REGISTERS_NAV_VIEW = 'registers_nav'
export const REGISTERS_STATUS_VIEW = 'registers_status'
export const REGISTERS_ROWS_VIEW = 'registers_rows'

export interface RegisterColumn {
  key: string
  label: string
  role: string
}

export type RegisterCells = Record<string, string | null>

export interface RegisterRow {
  register_key: string
  identity: string
  identity_unstable: boolean
  position: number
  revision: number
  cells: RegisterCells
  /** MYAC-1642 — the files its source and quality-agreement cells name that
   *  resolved, by file name; absent on rows projected before, or when none did. */
  document_handles?: Record<string, DocumentHandle>
}

export interface RegisterNavEntry {
  exists: boolean
  columns: RegisterColumn[]
  source_filenames: string[]
  /** MYAC-1642 — the read files that resolved, by name; absent before. */
  source_handles?: { filename: string; document_handle: DocumentHandle }[]
  revision: number
  row_count: number
  column_count: number
  last_run_at: ViewTimestamp
  used_fallback: boolean
  run_summary: string
}

export type RegisterPhase = 'idle' | 'running' | 'failed' | 'unknown'

/** Firestore hands timestamps back as `Timestamp`; the projection writes a datetime. */
type ViewTimestamp = { toMillis?: () => number } | string | null

export interface RegisterStatusEntry {
  phase: RegisterPhase
  last_error: string
  last_run_at: ViewTimestamp
  requested_by: string
}

function toMillis(value: ViewTimestamp): number | null {
  if (!value) return null
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    return Number.isNaN(parsed) ? null : parsed
  }
  return typeof value.toMillis === 'function' ? value.toMillis() : null
}

interface RegistersNavView {
  registers?: Record<string, RegisterNavEntry>
}
export interface RegisterReadinessCheck {
  key: string
  ok: boolean
  /** The CTA that fills a failing check — 'run_qms_assessment' navigates to the
   *  QMS referentials page (product decision 2026-08-29: a button, not a message). */
  action?: string
}
export interface RegisterReadiness {
  ready: boolean
  checks?: RegisterReadinessCheck[]
}
interface RegistersStatusView {
  registers?: Record<string, RegisterStatusEntry>
  readiness?: RegisterReadiness
}

/** How a register's refresh is triggered today — still an Assessment doc (the command
 *  queue is a later phase); kept behind the store so the swap to a command is one edit. */
type Trigger = (pid: string) => Promise<unknown>

export function defineRegisterViewStore(
  storeId: string,
  registerKey: RegisterKey,
  trigger: Trigger,
) {
  return defineStore(storeId, () => {
    const projectId: Ref<string | null> = ref(null)

    const navDoc = useViewDoc<RegistersNavView>(projectId, REGISTERS_NAV_VIEW)
    const statusDoc = useViewDoc<RegistersStatusView>(projectId, REGISTERS_STATUS_VIEW)
    // Always a selective filter (§3.2): the four registers share one row family.
    //
    // Deliberately NO `orderBy('position')`. Firestore needs a COMPOSITE index for an
    // equality filter plus an order-by on a different field, and composite indexes live
    // in the firebase repo — so the query failed `failed-precondition` on dev until this
    // was removed. Ordering is applied below instead, on the server-assigned `position`:
    // the ORDER is still the server's decision, the client only respects it. §3.2 bans
    // client-side DERIVATION; applying an explicit server-provided key is not that.
    const rowDocs = useViewRows<RegisterRow>(projectId, REGISTERS_ROWS_VIEW, () => [
      where('register_key', '==', registerKey),
    ])

    const orderedRows = computed<RegisterRow[]>(
      () => [...rowDocs.rows.value].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    )

    const nav = computed<RegisterNavEntry | null>(
      () => navDoc.data.value?.registers?.[registerKey] ?? null,
    )
    const status = computed<RegisterStatusEntry | null>(
      () => statusDoc.data.value?.registers?.[registerKey] ?? null,
    )

    /**
     * §3.7 — a failed read is NOT an empty register. The view must render an error, not
     * "nothing extracted yet", or a permission problem reads as a clean slate.
     */
    const loadError = computed<Error | null>(
      () => navDoc.error.value ?? rowDocs.error.value ?? statusDoc.error.value ?? null,
    )
    const isLoading = computed<boolean>(
      () => navDoc.pending.value || rowDocs.pending.value,
    )

    const columns = computed<RegisterColumn[]>(() => nav.value?.columns ?? [])
    /** In the server's order, via the projected `position` (see the query above). */
    // A row's handles ride under a non-column key (MYAC-1642), so they reach the
    // record drawer through every view's own row path, the supplier merge included.
    const rows = computed<RegisterRecord[]>(() =>
      orderedRows.value.map((r) =>
        r.document_handles ? { ...r.cells, [DOCUMENT_HANDLES_KEY]: r.document_handles } : r.cells,
      ),
    )
    const rowsDetailed = computed<RegisterRow[]>(() => orderedRows.value)

    const isRunning = computed<boolean>(() => status.value?.phase === 'running')
    const runFailed = computed<boolean>(() => status.value?.phase === 'failed')
    const runError = computed<string>(() => status.value?.last_error ?? '')

    // --- fact-only extraction gate (MYAC-1174 P1, server-computed) ----------
    const readiness = computed<RegisterReadiness | null>(
      () => statusDoc.data.value?.readiness ?? null,
    )
    /** Mirrors change-control's gate contract (MYAC-954: an unreadable gate is
     * an UNKNOWN gate, never a silent green light). The cold-start refresh
     * intent lives in `registersReadinessStore` (the shell fires ONE per
     * page); this per-register mirror only reads the shared status doc and
     * feeds the extract-guard belt below. */
    const readinessStatus = computed<'checking' | 'ready' | 'blocked' | 'unknown'>(() => {
      if (readiness.value) return readiness.value.ready ? 'ready' : 'blocked'
      if (statusDoc.pending.value) return 'checking'
      return 'unknown'
    })

    /**
     * The chat result card's contract: did THIS run finish, and how. `last_run_at` is
     * written only at a TERMINAL (idle / failed), never while running — which is exactly
     * what lets the card tell "my run finished" from "a previous run's result".
     * Replaces the old `_triggerStatus` / `_triggerUpdatedAt` read off the Assessment doc.
     */
    const lastRunStatus = computed<'running' | 'done' | 'failed' | null>(() => {
      switch (status.value?.phase) {
        case 'running': return 'running'
        case 'failed': return 'failed'
        case 'idle': return 'done'
        default: return null
      }
    })
    const lastRunAtMs = computed<number | null>(() => toMillis(status.value?.last_run_at ?? null))

    const usedFallback = computed<boolean>(() => nav.value?.used_fallback ?? false)
    const runSummary = computed<string | null>(() => nav.value?.run_summary || null)
    const hasExtraction = computed<boolean>(() => nav.value?.exists === true)
    /** The materialized count — not `rows.length`, which is only what is loaded. */
    const recordCount = computed<number>(() => nav.value?.row_count ?? 0)
    /** Run-level provenance the user recognises: its names, and where each one
     *  that resolved lives (MYAC-1642). */
    const sourceDocuments = computed<{ filename?: string; document_handle?: DocumentHandle }[]>(() => {
      const handles = new Map(
        (nav.value?.source_handles ?? []).map((h) => [h.filename, h.document_handle] as const),
      )
      return (nav.value?.source_filenames ?? []).map((filename) => {
        const document_handle = handles.get(filename)
        return document_handle ? { filename, document_handle } : { filename }
      })
    })

    /**
     * MYAC-796/899 chat auto-continue — the ONE thing still read from the Assessment
     * doc. The follow-up text and its consumed flag are a handshake between the chat and
     * the register page; they are not register CONTENT, so they have no place in the
     * read-model. They move to a `register_followup_ack` command in the commands phase,
     * and this subscription goes with them. Kept deliberately narrow: two fields, no
     * status, no rows — the view is the source of truth for everything else.
     */
    const chatFollowup = ref<string | null>(null)
    const chatFollowupConsumed = ref(false)
    const triggerRef = ref<DocumentReference | null>(null)
    let followupUnsub: Unsubscribe | null = null

    function stopFollowup(): void {
      followupUnsub?.()
      followupUnsub = null
      chatFollowup.value = null
      chatFollowupConsumed.value = false
      triggerRef.value = null
    }

    watch(projectId, (pid) => {
      stopFollowup()
      if (!pid) return
      const db = useFirestore()
      followupUnsub = onSnapshot(
        query(
          collection(db, `projects/${pid}/assessments/`),
          where('kind', '==', `qms_${registerKey}_list`),
          orderBy('updated_at', 'desc'),
          limit(1),
        ),
        (snap) => {
          const data = snap.docs[0]?.data()
          chatFollowup.value = (data?.chat_followup as string | undefined) ?? null
          chatFollowupConsumed.value = Boolean(data?.chat_followup_consumed)
          triggerRef.value = snap.docs[0]?.ref ?? null
        },
        (e) => logger.error(`[${registerKey}] follow-up subscription failed`, e),
      )
    })

    /** Mark the chat follow-up consumed so the auto-continue fires exactly once. */
    async function markFollowupConsumed(): Promise<void> {
      chatFollowupConsumed.value = true // optimistic — prevents a same-tick re-fire
      if (triggerRef.value) {
        await updateDoc(triggerRef.value, {
          chat_followup_consumed: true, updated_at: serverTimestamp(),
        }).catch((err) => logger.error('markFollowupConsumed write failed:', err))
      }
    }

    function subscribe(pid: string): void {
      projectId.value = pid
    }
    function unsubscribe(): void {
      projectId.value = null
      stopFollowup()
    }
    async function extract(pid: string): Promise<void> {
      if (readinessStatus.value === 'blocked') {
        logger.warn('register extraction blocked by the readiness gate')
        return
      }
      await trigger(pid)
      // `registerKey` comes from the factory closure, so the discriminator is free
      // and every register instance — present and future — is covered by this line.
      // Placed AFTER the trigger, not before: `trigger` is the per-register Firestore
      // write and can throw (MYAC-1138 — an emission never counts a merely-requested
      // action, only one that actually succeeded).
      try {
        trackAction(CsmAction.RegisterExtractionTriggered, {
          projectId: pid,
          projectMeta: { kind: registerKey },
        })
      } catch { }
    }

    return {
      // state
      projectId,
      // reads
      nav, status, columns, rows, rowsDetailed,
      isLoading, loadError,
      isRunning, runFailed, runError, lastRunStatus, lastRunAtMs,
      readiness, readinessStatus,
      usedFallback, runSummary, hasExtraction, recordCount, sourceDocuments,
      // chat auto-continue (still Assessment-doc backed — see above)
      chatFollowup, chatFollowupConsumed, markFollowupConsumed,
      // actions
      subscribe, unsubscribe, extract,
    }
  })
}

/** The four stores. Ids unchanged, so every existing `useQMS…Store()` call still works. */
export const useQMSCapaListStore = defineRegisterViewStore(
  'qmsCapaList', 'capa', (pid) => useAssessmentsStore().createCapaExtraction(pid))

export const useQMSNcListStore = defineRegisterViewStore(
  'qmsNcList', 'nc', (pid) => useAssessmentsStore().createNcExtraction(pid))

export const useQMSChangeControlListStore = defineRegisterViewStore(
  'qmsChangeControlList', 'change_control',
  (pid) => useAssessmentsStore().createChangeControlExtraction(pid))

export const useQMSSupplierListStore = defineRegisterViewStore(
  'qmsSupplierList', 'supplier',
  (pid) => useAssessmentsStore().createSupplierExtraction(pid))
